/*
 * Copyright 2022 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { BackstageTheme } from '@mainstage/theme';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import DeleteIcon from '@material-ui/icons/Delete';
import ExpandMoreIcon from '@material-ui/icons/ExpandLess';
import { usePrevious } from '@react-hookz/web';
import { useEffect, useState } from 'react';
import { useDryRun } from '../DryRunContext';
import { DryRunResultsView } from './DryRunResultsView';

const MAX_VISIBLE_RESULTS = 3;

const useStyles = makeStyles((theme: BackstageTheme) => ({
  accordion: {
    // MUI adds margin: 16px 0 on expanded accordions by default; remove it
    '&.Mui-expanded': {
      margin: 0,
    },
  },
  header: {
    height: 48,
    minHeight: 0,
    '&.Mui-expanded': {
      height: 48,
      minHeight: 0,
    },
  },
  headerContent: {
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexWrap: 'nowrap',
    overflow: 'hidden',
  },
  expandIconLeft: {
    order: -1,
    marginLeft: 0,
    marginRight: theme.spacing(0.5),
  },
  chips: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
    marginLeft: theme.spacing(1),
  },
  deleteIcon: {
    fontSize: 20,
  },
  content: {
    display: 'flex',
    background: theme.palette.background.default,
    padding: 0,
    height: 400,
    overflow: 'hidden',
    '& > *': { flex: 1 },
  },
}));

export function DryRunResults() {
  const classes = useStyles();
  const dryRun = useDryRun();
  const [expanded, setExpanded] = useState(false);
  const [hidden, setHidden] = useState(true);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<HTMLElement | null>(
    null,
  );

  const resultsLength = dryRun.results.length;
  const prevResultsLength = usePrevious(resultsLength);
  useEffect(() => {
    if (prevResultsLength === 0 && resultsLength === 1) {
      setHidden(false);
      setExpanded(true);
    } else if (prevResultsLength === 1 && resultsLength === 0) {
      setExpanded(false);
    }
  }, [prevResultsLength, resultsLength]);

  const reversed = [...dryRun.results].reverse();
  const visibleResults = reversed.slice(0, MAX_VISIBLE_RESULTS);
  const hiddenResults = reversed.slice(MAX_VISIBLE_RESULTS);

  return (
    <Accordion
      className={classes.accordion}
      variant="outlined"
      expanded={expanded}
      hidden={resultsLength === 0 && hidden}
      onChange={(_, exp) => setExpanded(exp)}
      onTransitionEnd={() => resultsLength === 0 && setHidden(true)}
    >
      <AccordionSummary
        className={classes.header}
        classes={{
          content: classes.headerContent,
          expandIcon: classes.expandIconLeft,
        }}
        expandIcon={<ExpandMoreIcon />}
      >
        <Typography>Dry Runs</Typography>
        <div className={classes.chips}>
          {visibleResults.map(result => (
            <Chip
              key={result.id}
              label={`Result ${result.id}`}
              variant={
                dryRun.selectedResult?.id === result.id ? 'default' : 'outlined'
              }
              color={
                dryRun.selectedResult?.id === result.id ? 'primary' : 'default'
              }
              onClick={e => {
                e.stopPropagation();
                dryRun.selectResult(result.id);
                (e.currentTarget as HTMLElement).blur();
              }}
              onDelete={e => {
                e.stopPropagation();
                dryRun.deleteResult(result.id);
              }}
              deleteIcon={<DeleteIcon className={classes.deleteIcon} />}
            />
          ))}
          {hiddenResults.length > 0 && (
            <>
              <Chip
                label="Older Results"
                variant="outlined"
                onClick={e => {
                  e.stopPropagation();
                  setMoreMenuAnchor(e.currentTarget as HTMLElement);
                }}
              />
              <Menu
                anchorEl={moreMenuAnchor}
                open={Boolean(moreMenuAnchor)}
                onClose={() => setMoreMenuAnchor(null)}
                onClick={e => e.stopPropagation()}
              >
                {hiddenResults.map(result => (
                  <MenuItem
                    key={result.id}
                    selected={dryRun.selectedResult?.id === result.id}
                    onClick={() => {
                      dryRun.selectResult(result.id);
                      setMoreMenuAnchor(null);
                    }}
                  >
                    {`Result ${result.id}`}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </div>
      </AccordionSummary>
      <Divider orientation="horizontal" />
      <AccordionDetails className={classes.content}>
        <DryRunResultsView />
      </AccordionDetails>
    </Accordion>
  );
}
